import { FC, useEffect, useState } from "react";
import Joi from "joi";
import { Box, Text, Stack, Heading } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { captureException } from "@sentry/nextjs";

import useForm, { OnFormSubmitType } from "hooks/useForm";
import useApiEndpoint from "hooks/useApiEndpoint";
import useGlobalState from "hooks/useGlobalState";
import useGlobalActions from "hooks/useGlobalActions";
import useHelperCRM, { TEventDataWatchdogCreated } from "hooks/useHelperCRM";
import { EApiWatchdogFrequencyId } from "utils/watchdog";
import CreateWatchdogFromOfferApiEndpoint from "utils/watchdog/api/CreateWatchdogFromOffer";
import { useTracking } from "contexts/Tracking";
import { resolveDogFreq } from "helpers/watchdog";
import { DISPOSITIONS } from "@ud/config/dispositions";
import { useCreateModal, InitialUseCreateModalType } from "../ModalsProvider";
import { useWatchdogCreatedModal } from "../useWatchdogCreatedModal";
import FormRadio from "components/Chakra/FormRadio";
import AnimatedInput from "components/Chakra/AnimatedInput";
import CustomLink from "components/Chakra/CustomLink";
import Button from "components/Chakra/Button";
import { EventPayload } from "src/types/types";
import { DetailData } from "@ud/api/newPhpApi/__generated__/newPhpApi.generated";

type WatchdogOfferModalProps = EventPayload & {
  offer: DetailData;
};

const WatchdogOfferModal: FC<WatchdogOfferModalProps> = ({
  offer,
  loginEvent,
}) => {
  const [showWatchdog, setShowWatchdog] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [frequencyId, setFrequencyId] = useState<string>(
    EApiWatchdogFrequencyId.ONCE_PER_DAY,
  );

  const { openModal: openWatchdogCreatedModal, closeAllModals } =
    useWatchdogCreatedModal();

  const { user } = useGlobalState();
  const { login } = useGlobalActions();
  const { trackEvent } = useTracking();
  const { createEventWatchdogCreated } = useHelperCRM();

  const { callEndpoint: createWatchdogApi } = useApiEndpoint(
    CreateWatchdogFromOfferApiEndpoint,
  );

  const { t } = useTranslation();

  useEffect(() => {
    trackEvent({
      "dl.eCat": "offer_detail",
      "dl.eAct": "dialog.shown",
      "dl.eLab": "Watchdog - založení",
    });
  }, []);

  const frequencyOptions = [
    {
      value: EApiWatchdogFrequencyId.IMMEDIATE,
      label: t("watchdogFrequency.immediate"),
      badge: "Nejoblíbenější",
    },
    {
      value: EApiWatchdogFrequencyId.ONCE_PER_DAY,
      label: t("watchdogFrequency.oncePerDay"),
    },
    {
      value: EApiWatchdogFrequencyId.ONCE_PER_WEEK,
      label: t("watchdogFrequency.oncePerWeek"),
    },
    {
      value: EApiWatchdogFrequencyId.NEVER,
      label: t("watchdogFrequency.never"),
    },
  ];

  interface IFormValues {
    email: string;
    password: string;
  }

  const formSchema = Joi.object({
    email: Joi.string().email({ tlds: false }).required().trim(),
    password: Joi.string().required(),
  });

  const {
    handleSubmit,
    registerWithError,
    watch,
    formState: { isSubmitting },
    getFieldState,
    clearErrors,
  } = useForm<IFormValues>(formSchema, {
    defaultValues: {
      email: user ? user.email : "",
      password: "",
    },
  });

  const handledWatchdog = async (): Promise<void> => {
    const email = watch("email");
    const password = watch("password");
    try {
      const { data } = await createWatchdogApi({
        offerId: offer.id,
        email,
        frequencyId,
      });
      clearErrors();
      if (data?.status === "ok" && loginEvent?.REGISTRATION && !password) {
        trackEvent(loginEvent.REGISTRATION);
      }

      if (data?.status === "error" && data?.error_id === "ERROR_EMAIL_EXISTS") {
        setErrorMessage(
          "Zadanou emailovou adresu již známe. Zadejte heslo prosím.",
        );
        setShowWatchdog(true);

        return;
      }

      const disposition = DISPOSITIONS.find((item) => {
        if (item.apiIds[0] === offer.dispositionId) {
          return item.translationId;
        }
      });
      const furnishings = offer.parameters.furnished?.title ?? "";
      const parseData: TEventDataWatchdogCreated = {
        city: offer.village?.name ?? "",
        dispositions: [disposition?.translationId ?? ""],
        furnishing: [furnishings],
        frequency: frequencyId,
        noCommission: false,
      };
      createEventWatchdogCreated({
        userEmail: email,
        eventData: parseData,
      });

      closeAllModals();
      trackEvent({
        "dl.eCat": "watchdog",
        "dl.eAct": "vytvoreni_detail_inzeratu",
        "dl.eLab": resolveDogFreq(frequencyId),
      });
      openWatchdogCreatedModal();
    } catch (err) {
      captureException(err);
    }
  };

  const handleData: OnFormSubmitType<IFormValues> = async ({
    email,
    password,
  }: IFormValues) => {
    if (email && password) {
      setLoadingLogin(true);
      const wasSuccessful = await login({
        email,
        password,
      });
      if (wasSuccessful) {
        return handledWatchdog();
      } else {
        setErrorMessage("Přihlášení se nezdařilo");
      }
    } else {
      return handledWatchdog();
    }
  };

  const handleFrequency = (value: string) => {
    setFrequencyId(value);
  };

  const emailFieldState = getFieldState("email");

  return (
    <Box textAlign="center" data-test="dogModalContent">
      <Heading as="h3" data-test="dogModalHeading">
        Hlídací pes hlídá inzeráty za vás
      </Heading>
      <Text mt="10px" mb="20px">
        Každý nový inzerát vyčmuchá <br />a pošle rovnou na e-mail.
      </Text>

      <form onSubmit={handleSubmit(handleData)}>
        <Stack my="15px" data-test="notificationDogSettingModal">
          <Text
            align="left"
            textTransform="uppercase"
            color="ud-grey-600"
            fontWeight="500"
            variant="form-label"
          >
            Interval upozornění
          </Text>

          <Box
            p="16px"
            borderRadius="6px"
            border="1px solid"
            borderColor="ud-grey-300"
          >
            <FormRadio
              items={frequencyOptions.filter(
                (x) => x.value !== EApiWatchdogFrequencyId.NEVER,
              )}
              value={frequencyId}
              onChange={handleFrequency}
            />
          </Box>
        </Stack>

        <Stack spacing="15px" mt="20px">
          {(!user || loadingLogin) && (
            <Box>
              <AnimatedInput
                {...registerWithError("email")}
                label="E-mail"
                autoComplete="email"
                required
                data-test="registrationEmailInput"
              />
              {showWatchdog && (
                <AnimatedInput
                  {...registerWithError("password")}
                  label="Heslo"
                  type="password"
                  autoComplete={showWatchdog ? "password" : "current-password"}
                  required
                />
              )}
            </Box>
          )}
        </Stack>

        {errorMessage && (
          <Box>
            <Text
              width="300px"
              margin="auto"
              lineHeight="130%"
              textColor="ud-error"
              mb="15px"
            >
              {errorMessage}
            </Text>
          </Box>
        )}

        <Text mb="5px" variant="small_2" data-test="privacyData">
          Kliknutím souhlasíte s našimi{" "}
          <CustomLink showUnderline to="/zpracovani-osobnich-udaju">
            zásadami ochrany osobních údajů.
          </CustomLink>
        </Text>

        <Stack mt={4} direction="row" justify="center">
          <Button
            data-test="saveDogButton"
            type="submit"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            onClick={() => {
              trackEvent({
                "dl.eCat": "offer_detail",
                "dl.eAct": "watchdog.clicked",
                "dl.eLab": "Watchdog - Uložit psa",
              });
              ((!emailFieldState.error && emailFieldState.isDirty) || user) &&
                handledWatchdog();
            }}
          >
            Uložit psa
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

export const useWatchdogOfferModal: InitialUseCreateModalType<
  WatchdogOfferModalProps
> = () =>
  useCreateModal(
    "watchdogOfferModal",
    WatchdogOfferModal as React.FC,
    null,
    null,
    {
      size: "lg",
      variant: "primary",
    },
  );
